import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'nav.dashboard', icon: 'ri-dashboard-line' },
  { path: '/satislar', label: 'nav.sales', icon: 'ri-shopping-bag-line' },
  { path: '/veresiye', label: 'nav.credit', icon: 'ri-time-line' },
  { path: '/tedarikciler', label: 'nav.suppliers', icon: 'ri-truck-line' },
  { path: '/giderler', label: 'nav.expenses', icon: 'ri-bill-line' },
  { path: '/gelirler', label: 'nav.incomes', icon: 'ri-coins-line' },
  { path: '/kasa', label: 'nav.cash', icon: 'ri-safe-2-line' },
  { path: '/raporlar', label: 'nav.reports', icon: 'ri-bar-chart-line' },
  { path: '/ayarlar', label: 'nav.settings', icon: 'ri-settings-3-line' },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <div className="flex h-screen bg-cream-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-cream-200 h-full fixed left-0 top-0 z-30">
        <div className="p-6 border-b border-cream-200">
          <h1 className="font-display text-xl font-semibold text-boutique-black tracking-tight">
            {t('app.title')}
          </h1>
          <p className="text-xs text-boutique-warm mt-1 font-medium tracking-wide uppercase">
            {t('app.subtitle')}
          </p>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-gold-50 text-gold-600 border-l-2 border-gold-400'
                        : 'text-boutique-warm hover:bg-cream-100 hover:text-boutique-black'
                    }`
                  }
                >
                  <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-base`} />
                  <span>{t(item.label)}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-cream-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-gold-100 flex items-center justify-center text-gold-600 font-semibold text-sm">
              {user?.fullName?.charAt(0) || 'H'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-boutique-black truncate">{user?.fullName || 'Hadiye Kılıç'}</p>
              <p className="text-xs text-boutique-warm truncate">{user?.email || 'admin@butik.com'}</p>
            </div>
            <button
              onClick={logout}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:text-red-500 hover:bg-red-50 transition-colors"
              title={t('nav.logout')}
            >
              <i className="ri-logout-box-line text-lg" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-cream-200 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-boutique-black hover:bg-cream-100 transition-colors"
          >
            <i className="ri-menu-line text-xl" />
          </button>
          <div>
            <h1 className="font-display text-lg font-semibold text-boutique-black">{t('app.title')}</h1>
          </div>
        </div>
        <div className="w-9 h-9 rounded-full bg-gold-100 flex items-center justify-center text-gold-600 font-semibold text-sm">
          {user?.fullName?.charAt(0) || 'H'}
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/30 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="lg:hidden fixed left-0 top-0 bottom-0 w-72 bg-white z-50 shadow-xl flex flex-col">
            <div className="p-5 border-b border-cream-200 flex items-center justify-between">
              <div>
                <h1 className="font-display text-xl font-semibold text-boutique-black">{t('app.title')}</h1>
                <p className="text-xs text-boutique-warm mt-1 font-medium tracking-wide uppercase">
                  {t('app.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3">
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-gold-50 text-gold-600 border-l-2 border-gold-400'
                            : 'text-boutique-warm hover:bg-cream-100 hover:text-boutique-black'
                        }`
                      }
                    >
                      <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-base`} />
                      <span>{t(item.label)}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="p-4 border-t border-cream-200">
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-9 h-9 rounded-full bg-gold-100 flex items-center justify-center text-gold-600 font-semibold text-sm">
                  {user?.fullName?.charAt(0) || 'H'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-boutique-black truncate">{user?.fullName || 'Hadiye Kılıç'}</p>
                  <p className="text-xs text-boutique-warm truncate">{user?.email || 'admin@butik.com'}</p>
                </div>
                <button
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <i className="ri-logout-box-line text-lg" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 overflow-y-auto pt-16 lg:pt-0 h-full">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}